import { Col, Row } from "react-grid-system";
import { theme } from "../../../theme";
import TitleSmall from "../../atoms/typography/TitleSmall";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { useState, useEffect } from "react";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import { useTranslation } from "react-i18next";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import { SortingArrowsIcon } from "../../atoms/icons/SortingArrowsIcon";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import styled from "styled-components";
import BodySmall from "../../atoms/typography/BodySmall";
import dayjs from "dayjs";
import { formatPrice, getCurrencySymbol } from "../../../utils/formatters";
import TextField from "../../atoms/inputs/TextField";
import MultiSelect from "../../atoms/inputs/MultiSelect";
import PaginationComponent from "../PaginationComponent";
import { getInvoiceListStatusBadge } from "../../../utils/invoicesHelpers";
import type { InvoiceProcessStatus } from "../../../models/InvoiceStatusModel";

export interface RecievedInvoice {
  id: string;
  status: InvoiceProcessStatus;
  invoiceNumber: string;
  issuerName: string;
  invoiceDate: Date;
  dueDate: Date;
  amount: number;
  currency?: string;
}

interface Props {
  invoices: Array<RecievedInvoice>;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const InvoiceRow = styled(Row)`
  padding: 12px 8px;
  border-bottom: 1px solid ${theme.colors.grey05};

  &:hover {
    background-color: ${theme.colors.hover};
  }
`;

const statusOptions: Array<{ value: string; label: string }> = [
  {
    value: "Avräkning",
    label: "assessment.settlement",
  },
  {
    value: "Avvisad",
    label: "documentHandler.rejected",
  },
  {
    value: "Mottagen",
    label: "documentHandler.recieved",
  },
];

const RecievedInvoicesList: React.FC<Props> = ({
  invoices,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (invoices.length > 0) setOpen(true);
  }, [invoices.length]);
  const [selectedStatuses, setSelectedStatuses] = useState<
    Array<string> | undefined
  >(undefined);

  const onSelectMultiselections = (selectedOptions: string[]) => {
    setSelectedStatuses(selectedOptions);
  };

  return (
    <div>
      <Row nogutter align="center" justify="between">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <TitleSmall color={theme.colors.primary}>
            {t("invoiceAssessment.recievedOverdueInvoices")}
          </TitleSmall>
          {invoices.length === 0 ? (
            <BodySmall color={theme.colors.textSecondary}>
              <i>{t("invoiceAssessment.noReceivedOverdueInvoices")}</i>
            </BodySmall>
          ) : // TODO: show total sum
          null}
        </div>
        <ChevronButton
          type={open ? "up" : "down"}
          onClick={() => setOpen(!open)}
        />
      </Row>

      <SlideOutHeight isVisible={open}>
        <HorizontalSpacer spacing={1} />
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <TextField placeholder={t("common.search")} />
          <MultiSelect
            options={statusOptions}
            selectedOptions={selectedStatuses ?? []}
            onChange={(selectedOption) =>
              onSelectMultiselections(selectedOption)
            }
            onClickSelectAll={() => setSelectedStatuses(undefined)}
            title={t("common.status")}
          />
        </div>
        <HorizontalSpacer spacing={1} />
        <Row
          style={{
            padding: 8,
            borderBottom: `1px solid ${theme.colors.grey05}`,
          }}
          nogutter
          align="center"
        >
          <Col lg={2} style={{ display: "flex", alignItems: "center" }}>
            <TableHeaderText>{t("invoices.invoiceNumber")}</TableHeaderText>
          </Col>
          <Col lg={2} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TableHeaderText>{t("invoices.issuer")}</TableHeaderText>
          </Col>
          <Col lg={2} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TableHeaderText>{t("invoices.invoiceDate")}</TableHeaderText>
            <SortingArrowsIcon />
          </Col>
          <Col lg={2} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TableHeaderText>{t("invoices.dueDate")}</TableHeaderText>
            <SortingArrowsIcon />
          </Col>
          <Col
            lg={1.8}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <TableHeaderText>{t("invoices.amount")}</TableHeaderText>
            <SortingArrowsIcon />
          </Col>
          <Col lg={2} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TableHeaderText>{t("common.status")}</TableHeaderText>
            <SortingArrowsIcon />
          </Col>
          <Col lg={0.2}></Col>
        </Row>
        {invoices.map((invoice) => (
          <InvoiceRow key={invoice.id} nogutter>
            <Col lg={2}>
              <BodySmall>{invoice.invoiceNumber}</BodySmall>
            </Col>
            <Col lg={2}>
              <BodySmall>{invoice.issuerName}</BodySmall>
            </Col>
            <Col lg={2}>
              <BodySmall>
                {dayjs(invoice.invoiceDate).format("YYYY-MM-DD")}
              </BodySmall>
            </Col>
            <Col lg={2}>
              <BodySmall>
                {dayjs(invoice.dueDate).format("YYYY-MM-DD")}
              </BodySmall>
            </Col>
            <Col lg={1.8}>
              <BodySmall>
                {formatPrice(invoice.amount)}{" "}
                {getCurrencySymbol(invoice.currency)}
              </BodySmall>
            </Col>
            <Col lg={2}>{getInvoiceListStatusBadge(invoice.status)}</Col>
            <Col lg={0.2}>
              <ChevronSmallRightIcon />
            </Col>
          </InvoiceRow>
        ))}
        <HorizontalSpacer spacing={1} />
        <Row nogutter justify="end">
          <PaginationComponent
            numberOfPages={totalPages}
            currentPageIndex={currentPage}
            setCurrentPageIndex={onPageChange}
          />
        </Row>
      </SlideOutHeight>
    </div>
  );
};

export default RecievedInvoicesList;
